<script setup lang="ts">
import { ChevronRight } from 'lucide-vue-next'

/**
 * ListRow — generic home-screen list row. Mirrors core/ui/listitems/ListRow.kt.
 *
 * Layout: top hairline · leading slot · 12px gap · title + meta column ·
 * trailing slots · optional chevron-right.
 *
 * Padding 12px top / 20px left / 12px bottom / 16px right.
 *
 * The list as a whole closes with a hairline below the last row — render
 * one via <hr class="list-hairline" /> at the call site (matches Android,
 * where the caller is responsible for the closing divider).
 */

withDefaults(defineProps<{
  /** Render to a router-link (set `to`) or button (set `onClick`). */
  to?: string | object
  showChevron?: boolean
  showTopDivider?: boolean
}>(), {
  showChevron: true,
  showTopDivider: true,
})

defineEmits<{ click: [event: MouseEvent] }>()
</script>

<template>
  <div class="list-row-wrapper" :class="{ 'list-row-divided': showTopDivider }">
    <component
      :is="to ? 'router-link' : 'button'"
      :to="to"
      :type="to ? undefined : 'button'"
      class="list-row"
      @click="$emit('click', $event)"
    >
      <span class="list-row-leading">
        <slot name="leading" />
      </span>
      <span class="list-row-body">
        <span class="list-row-title">
          <slot name="title" />
        </span>
        <span v-if="$slots.meta" class="list-row-meta">
          <slot name="meta" />
        </span>
      </span>
      <span v-if="$slots.trailing" class="list-row-trailing">
        <slot name="trailing" />
      </span>
      <ChevronRight
        v-if="showChevron"
        :size="14"
        class="list-row-chevron"
        aria-hidden="true"
      />
    </component>
  </div>
</template>

<style scoped>
.list-row-wrapper {
  display: block;
  width: 100%;
}

.list-row-divided {
  border-top: 1px solid var(--hairline);
}

.list-row {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px 12px 20px;
  background: transparent;
  border: 0;
  text-align: left;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition: background-color var(--motion-fast);
  font-family: inherit;
}

.list-row:hover {
  background: var(--surface-muted);
}

.list-row:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: -2px;
}

.list-row-leading {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.list-row-body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  margin-left: var(--space-md);
  gap: var(--space-2);
}

.list-row-title {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  font-family: var(--font-sans);
  font-size: 15px;
  line-height: 20px;
  font-weight: var(--weight-semibold);
  color: var(--ink-900);
  min-width: 0;
}

.list-row-title :deep(.list-row-title-secondary) {
  font-weight: var(--weight-regular);
  color: var(--ink-600);
}

.list-row-meta {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  font-family: var(--font-sans);
  font-size: 12.5px;
  line-height: 17px;
  color: var(--ink-600);
  min-width: 0;
}

.list-row-trailing {
  display: inline-flex;
  align-items: center;
  margin-left: var(--space-md);
  flex-shrink: 0;
}

.list-row-chevron {
  margin-left: var(--space-md);
  color: var(--ink-400);
  flex-shrink: 0;
}
</style>
